import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageSeo from '../components/PageSeo';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BrandHeroEyebrow from '../components/BrandHeroEyebrow';
import { brandHeroSection, heroContentZ } from '../theme/heroStyles';

const ContactPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Build request body, only including fields that have values
      const requestBody = {
        name: formData.name,
        email: formData.email,
        message: formData.message
      };

      // Only add optional fields if they have values
      if (formData.phone && formData.phone.trim()) {
        requestBody.phone = formData.phone.trim();
      }
      if (formData.company && formData.company.trim()) {
        requestBody.company = formData.company.trim();
      }

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSnackbar({
          open: true,
          message: data.message || 'Thank you for your message! We\'ll get back to you soon.',
          severity: 'success'
        });
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        // Show specific validation errors if available
        let errorMessage = data.message || 'Failed to send message. Please try again later.';
        if (data.errors && data.errors.length > 0) {
          const errorDetails = data.errors.map(err => {
            const fieldName = err.field.charAt(0).toUpperCase() + err.field.slice(1);
            return `${fieldName}: ${err.message}`;
          }).join('; ');
          errorMessage = `${errorMessage} ${errorDetails}`;
        }
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again later.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Email Us',
      detail: 'dieselct2@mweb.co.za',
      description: 'Send us an email anytime'
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Call Us',
      detail: '011 493-6226',
      description: 'Main office line'
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Mobile',
      detail: '081 246 7394',
      description: 'Cell phone for urgent matters'
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Visit Us',
      detail: '46 John Street, Selby, Johannesburg, 2001',
      description: 'Schedule an in-person meeting'
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
      title: 'Business Hours',
      detail: 'Monday – Thursday: 07h00 – 16h30\nFriday: 07h00 – 15h30\nSat/Sun: Closed\nPublic holidays: Closed',
      description: 'Schedule your visit during our operating hours'
    }
  ];

  const faqs = [
    {
      question: 'What services does Diesel City offer?',
      answer: 'We offer comprehensive diesel services including repair and maintenance of diesel pumps, injectors and turbochargers, engine overhauls, diesel injection services, general servicing of diesel vehicles, electronic diagnostic troubleshooting and repairs, injector coding, and injectors and turbo chargers.'
    },
    {
      question: 'How long does a typical repair take?',
      answer: 'Repair times vary depending on the complexity and availability of parts. Simple repairs might take 1-2 days, while complex engine overhauls can take 1-2 weeks. We provide detailed timelines during our initial assessment.'
    },
    {
      question: 'Do you work on all makes of vehicles?',
      answer: 'Yes! We are fully equipped to carry out testing and repairs on all makes of vehicles and earth moving machinery for all mechanical in-line pumps and injectors, both electronic and mechanical governors.'
    },
    {
      question: 'What warranty do you provide?',
      answer: 'Six months on labour and new parts fitted. We assure our customers of quality workmanship and service standards at all times.'
    },
    {
      question: 'Are you certified?',
      answer: 'Yes, we are a member of the RMI (Retail Motor Industry) and affiliated to ARA (Automotive Remanufacture Association). We maintain the highest industry standards in all our work.'
    }
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <PageSeo
        pathname="/contact"
        title="Contact Diesel City | Selby, Johannesburg"
        description="Call 011 493 6226 or visit 46 John Street, Selby, Johannesburg. Diesel pump repair, overhauls and diagnostics. Mon–Thu 07:00–16:30, Fri 07:00–15:30."
        ogTitle="Contact Diesel City"
        ogDescription="Book diesel work, request a quote or ask about our services — expert team in Johannesburg."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <Box
        sx={{
          ...brandHeroSection(theme, '/assets/machineReplace.jpg'),
        }}
      >
        <Container maxWidth="lg" sx={heroContentZ}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <BrandHeroEyebrow label="Get in touch" />
              </Box>
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                gutterBottom
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Contact
                <Box component="div" sx={{
                  color: 'secondary.main',
                  display: 'block',
                  mt: 1,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}>
                  Us
                </Box>
              </Typography>
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  lineHeight: 1.7,
                  maxWidth: 500
                }}
              >
                We’re here to help!  Get in touch with us to discuss all your diesel fuel injection requirements.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: { xs: 300, md: 500 },
                  position: 'relative'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    maxWidth: 500,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    border: '4px solid rgba(255,255,255,0.2)'
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/machineReplace.jpeg"
                    alt="Contact Diesel City"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 2,
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Form & Info */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider',
                  p: { xs: 3, md: 4 }
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: { xs: '1.75rem', md: '2.25rem' }
                    }}
                  >
                    Send Us a Message
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '1rem', md: '1.1rem' }
                    }}
                  >
                    Fill out the form below and we'll get back to you within 24 hours
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        variant="outlined"
                        placeholder="e.g., +27 11 123 4567"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        multiline
                        rows={4}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        endIcon={<SendIcon />}
                        disabled={isSubmitting}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          px: 4,
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          borderRadius: 2,
                          boxShadow: '0 4px 15px rgba(23, 28, 143, 0.3)',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 20px rgba(23, 28, 143, 0.4)',
                          },
                          '&:disabled': {
                            backgroundColor: 'action.disabledBackground',
                            color: 'action.disabled'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} lg={5}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: { xs: '1.75rem', md: '2.25rem' }
                  }}
                >
                  Get In Touch
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.secondary',
                    mb: 4,
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}
                >
                  Multiple ways to reach us
                </Typography>
              </Box>

              <Stack spacing={3}>
                {contactInfo.map((info, index) => (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: 2,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        transition: 'all 0.2s ease'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ mt: 0.5 }}>
                          {info.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '1rem', md: '1.1rem' }
                            }}
                          >
                            {info.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 600,
                              mb: 1,
                              fontSize: { xs: '0.875rem', md: '1rem' },
                              whiteSpace: 'pre-line'
                            }}
                          >
                            {info.detail}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontSize: { xs: '0.875rem', md: '1rem' }
                            }}
                          >
                            {info.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faqs" component="section" aria-labelledby="faqs-heading" sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              id="faqs-heading"
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Find answers to common questions about our services
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8} sx={{ mx: 'auto' }}>
              {faqs.map((faq, index) => (
                <Accordion
                  key={index}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:before': { display: 'none' },
                    '&:hover': {
                      boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                      transition: 'all 0.2s ease'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        margin: '16px 0',
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1rem', md: '1.1rem' }
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'secondary.contrastText',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/assets/IMG-20250819-WA0034.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 5,
                opacity: 0.95,
                maxWidth: 700,
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              Don't wait to get your diesel vehicle or machinery back in top condition. Contact us today and let's discuss
              how we can help you with expert diesel services.
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              size="large"
              endIcon={<SendIcon />}
              sx={{
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(23, 28, 143, 0.4)',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(23, 28, 143, 0.6)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              Contact Us Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactPage;
